import React, { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Heart, Leaf, Users, ArrowRight, CheckCircle, Star, Phone, Mail, Globe, Flower2, BookOpen, Coffee, Home, Brain, Sparkles, Compass, Baby, Target, Moon, Sprout, Shield, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import heroImage from '@/assets/hero_image.jpg';
import { Badge } from '@/components/ui/badge';

const PreconceptionPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Preconception Care & Fertility Prep | Currimundi Clinic";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Prepare your body for conception with Chinese Medicine in Currimundi. Cycle awareness and fertility preparation on the Sunshine Coast.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Preconception Care & Fertility Prep | Currimundi Clinic");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Prepare your body for conception with Chinese Medicine in Currimundi. Cycle awareness and fertility preparation on the Sunshine Coast.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/preconception");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/preconception-care-chinese-medicine.jpg");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Preconception Care & Fertility Prep | Currimundi Clinic");
    }

    // Add LocalBusiness Schema for preconception services
    const preconceptionSchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Preconception Care & Fertility Preparation",
      "image": "https://womenandtheway.com.au/preconception-care-chinese-medicine.jpg",
      "description": "Prepare your body for conception with Chinese Medicine in Currimundi. Cycle awareness and fertility preparation on the Sunshine Coast.",
      "url": "https://womenandtheway.com.au/preconception",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Preconception Care, Cycle Awareness, Fertility Preparation, Acupuncture, Chinese Medicine",
      "areaServed": "Sunshine Coast, Currimundi, Caloundra, Maroochydore",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(preconceptionSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Preconception Care & Fertility Preparation
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Prepare your body, mind, and spirit for conception. We're here to support your fertility journey with gentle, evidence-based care.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  The preconception period is a unique window of opportunity to optimize your health and create the best possible foundation for pregnancy. At Women and the Way, we guide you through this important time with gentle, evidence-based care that honors your body's natural wisdom.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're planning to conceive in the next few months or just want to prepare your body for future fertility, our preconception care addresses the whole person - physical health, emotional wellbeing, and lifestyle factors. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of reproductive health. Many of our clients also benefit from our <a href="/fertility-support" className="text-sage-600 hover:text-sage-800 underline transition-colors">Fertility Support</a>, <a href="/ivf-support" className="text-sage-600 hover:text-sage-800 underline transition-colors">IVF Support</a>, and <a href="/emotional-healing" className="text-sage-600 hover:text-sage-800 underline transition-colors">Emotional Healing</a>.
                </p>
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : '🌱 Start where you are. We\'ll walk with you from there.'}</span>
                  <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={heroImage} 
                  alt="Preconception care and fertility preparation consultation with Chinese Medicine in Currimundi clinic" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Preconception Care Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Heart className="w-8 h-8 text-sage-600" />
                Why Preconception Care Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                Research shows that preconception care can improve fertility outcomes, support healthy egg quality, regulate cycles, and create a nourishing environment for conception. But beyond the science, we offer something equally important: a space to connect with your body, honour your fertility, and feel supported in your journey to parenthood.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                At Women and the Way, we understand that preconception care isn't just about protocols - it's about honouring your body's wisdom, supporting your natural fertility, and helping you feel confident and prepared for this beautiful transition.
              </p>
            </div>
          </div>
        </section>

        {/* What We Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Support
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Target className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Egg Quality Optimization</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Nourishing egg quality through diet, lifestyle, and acupuncture</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Stress Management</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Reducing stress hormones that can impact fertility</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Baby className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Nutrition & Lifestyle</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Fertility-supporting foods and healthy habits</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Immune Balance</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Supporting immune system health for optimal fertility</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Moon className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Sleep & Recovery</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Restorative sleep and hormonal balance support</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Preconception Care In-Clinic or Online
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* In-Clinic Support */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Fertility Acupuncture</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Regular sessions to support cycle regulation and egg quality</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Chinese Herbal Medicine</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Custom formulas to support fertility and hormonal balance</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Cycle Tracking Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Guidance on tracking ovulation and understanding your fertility</p>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Nutrition & Lifestyle Guidance</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Evidence-based recommendations for optimal fertility</p>
                </CardContent>
              </Card>

              {/* Online Sessions */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageCircle className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Telehealth Consultations</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Fertility guidance and support throughout your preconception process</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Herbal Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Custom formulas mailed to your door for ongoing fertility support</p>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Fertility Resources</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Cycle tracking guides, nutrition plans, and lifestyle recommendations</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Preconception Timeline */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                Preconception Timeline
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sage-600 font-bold">1</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Initial Assessment</h3>
                  <p className="text-sage-700">Comprehensive fertility evaluation and cycle analysis</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sage-600 font-bold">2</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">3-Month Preparation</h3>
                  <p className="text-sage-700">Weekly sessions to optimize egg quality and cycle regulation</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sage-600 font-bold">3</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Ovulation Support</h3>
                  <p className="text-sage-700">Acupuncture and herbs to support healthy ovulation</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl text-center">
                <CardContent className="p-8">
                  <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sage-600 font-bold">4</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Conception Ready</h3>
                  <p className="text-sage-700">Optimized fertility and prepared for conception</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6">
              Book Your Preconception Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Plant the seed now - prepare your body, heart, and hormones for what's to come.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
              onClick={handleBooking}
              disabled={isLoading}
            >
              <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : 'Book Preconception Session'}</span>
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </Button>
          </div>
        </section>
        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default PreconceptionPage; 